import { z } from "zod";
import { tablePolicySchema } from "../schemas/table-policy";
import { transferRouteConfigSchema } from "../transfer";

export const activitySchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  image: z.string().url().nullable(),
  // Full gallery for card photo pagers (the big Home card swipes through
  // these). Optional: most producers still send only the single `image`.
  images: z.array(z.string()).nullable().optional(),
  city: z.object({ id: z.string(), name: z.string() }).nullable(),
  // Category powers the card badge (web ActivityCard2 parity); optional so
  // older payloads without it still validate.
  category: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  // Lets `sortByVibes` (packages/shared/src/vibes) reorder Activity rows by
  // the signed-in user's picked vibes without a per-consumer adapter type —
  // optional/nullable since most Activity producers don't populate it yet.
  categorySlug: z.string().nullable().optional(),
  // An explore row's discover segment (restaurants, locations, ...). The
  // website builds an explore item's link from it. Optional: most producers
  // leave it out.
  segment: z.string().nullable().optional(),
  // Whether the row is something you can reserve. Explore rows are editorial:
  // no packages, no price, no booking CTA. `activityDetailSchema` has carried
  // this for a while; the list schema did not, so every card had to assume
  // bookable and shipped a "Reserver" button and "0 MAD" on venues that sell
  // nothing (owner, 2026-09-10). Optional, and absent means bookable, so
  // producers that never set it are unchanged.
  mode: z.enum(["bookable", "explore"]).nullable().optional(),
  price: z.number().nonnegative(),
  priceType: z.number().int().min(0).max(3),
  currency: z.string(),
  rating: z.number().min(0).max(5).nullable(),
  reviewCount: z.number().int().nonnegative().default(0),
  isSaved: z.boolean().default(false),
  // Added to the site in the last 30 days; the server decides, cards show a
  // gold "New" pill. Optional so producers that never set it show nothing.
  isNew: z.boolean().optional(),
});
export type Activity = z.infer<typeof activitySchema>;

/**
 * A guest-type or transport price.
 *
 * `-1` is the documented "this category is not offered" sentinel (see
 * `priceOrZero` in apps/majourneys — it maps a negative price to a zero
 * contribution). It is written by admin and returned verbatim by the API, so
 * the schema has to accept it: `nonnegative()` here rejected the response
 * outright, and because mobile parses the detail response through this schema,
 * every activity with a disabled child/infant/transport price failed to load
 * and rendered the generic "something went wrong" screen instead — 85 of 151
 * bookable activities in production on 2026-08-07, including every villa, most
 * quad rides and all the desert camps.
 */
const guestPrice = z.number().min(-1);

export const activityDetailPackageSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().default(""),
  timeStart: z.string().nullish(),
  timeEnd: z.string().nullish(),
  /** Exact departures ("HH:mm"). Beats the timeStart/timeEnd window. */
  timeSlots: z.array(z.string()).nullish(),
  /** One departure between timeStart and timeEnd, hour confirmed later. */
  flexibleTime: z.boolean().nullish(),
  maxGuests: z.number().int().nonnegative().default(0),
  /**
   * Per-vehicle packages: riders the flat rate pays for, as opposed to how
   * many fit (`maxGuests`). A 300cc quad seats 2 but is priced for 1.
   * `null`/absent = the rate covers the whole unit.
   */
  includedGuests: z.number().int().nullish(),
  isMostPopular: z.boolean().default(false),
  typePrice: z.number().int().min(0).max(3).default(0),
  pricing: z.object({
    adult: guestPrice.default(0),
    child: guestPrice.default(0),
    infant: guestPrice.default(0),
    transport: guestPrice.nullable().default(null),
    // Large-group pickup price (8..12 guests). Absent/null on flat-pickup venues.
    transportLarge: guestPrice.nullable().optional(),
    // Surcharge added when the guest stays outside the activity's city.
    transportOutside: guestPrice.nullable().optional(),
    // Per rider beyond `includedGuests` sharing the same unit (a second person
    // on a 300cc quad). Absent/null/0 = sharing is not sold.
    extraGuest: guestPrice.nullable().optional(),
  }),
});
export type ActivityDetailPackage = z.infer<typeof activityDetailPackageSchema>;

export const activityDetailSchema = activitySchema.extend({
  subtitle: z.string().nullable().default(null),
  // Explore-mode rows (restaurants, stays, locations…) are editorial-only — the
  // consumer hides packages / booking CTA / price when mode === "explore".
  // Optional so lighter ActivityDetail builders don't have to supply them;
  // absent is treated as bookable.
  mode: z.enum(["bookable", "explore"]).optional(),
  segment: z.string().nullable().optional(),
  category: z
    .object({ id: z.string(), name: z.string(), slug: z.string() })
    .nullable()
    .default(null),
  cityName: z.string().default(""),
  citySlug: z.string().nullable().default(null),
  // Media must never be able to take down a whole page. `.url()` rejects any
  // non-URL string, and without a fallback a single malformed row threw
  // `invalid_response` for the entire activity detail response — the screen
  // then rendered the generic "something went wrong" error. This is the same
  // failure class that took out 85 of 151 bookable activities in production on
  // 2026-08-07 (see the guestPrice comment above); `.catch` degrades a bad
  // media array to an empty gallery instead of an unusable page.
  gallery: z.array(z.string().url()).catch([]),
  images: z.array(z.string().url()).default([]).catch([]),
  overviewHtml: z.string(),
  duration: z.string().nullable(),
  isMealIncluded: z.boolean(),
  instantConfirmation: z.boolean(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  latitude: z.number().nullable().default(null),
  longitude: z.number().nullable().default(null),
  /** The venue keeps its exact location private until booking. */
  hideLocation: z.boolean().optional(),
  reservePayLater: z.boolean().default(false),
  /** Bookings are closed for this activity — the page still renders, the
   *  booking control is disabled. Resolved server-side against today. */
  bookingPaused: z.boolean().default(false),
  bookingPausedNote: z.string().nullable().default(null),
  freeCancellation: z.boolean().default(false),
  whatsappConfirmation: z.boolean().default(false),
  /**
   * The provider takes cash only on the day — no card machine.
   *
   * The emails have honoured this for a while; the clients could not, because
   * this endpoint never sent it, so both promised "cash or card" to everyone.
   */
  cashOnlyOnArrival: z.boolean().default(false),
  /**
   * A venue's own guest age bands, when it sets them (`activities.infant_max_age`
   * / `children_max_age`). Null means "use the site-wide brackets". Declared
   * here because zod strips undeclared keys — undeclared, these reached the app
   * and were silently discarded, so a pool that prices 13-year-olds as children
   * still showed them at the adult rate. Feed them to `ageBracketLabels`.
   */
  infantMaxAge: z.number().nullable().default(null),
  childrenMaxAge: z.number().nullable().default(null),
  /**
   * Smallest party the activity takes (adults + children; infants do not
   * count). 1 = no minimum. Optional so older payloads still parse; read it
   * through `normalizeMinPeople`.
   */
  minPeople: z.number().nullish(),
  template: z.string().nullable().default(null),
  /**
   * A restaurant's seatings ("19:00", "19:30", …) and its house rules. The
   * table flow reads both: seatings replace the package's time window, and the
   * policy decides when our team may send a payment link (large tables) or
   * when children are not seated. Optional so older payloads still parse.
   */
  sessionTimes: z.array(z.string()).nullish(),
  /** Weekdays bookings are taken on ("friday", …). Null or empty means every day. */
  openDays: z.array(z.string()).nullish(),
  minNoticeHours: z.number().int().nullish(),
  /**
   * `activities.opening_hours`, raw. Unknown rather than the strict schema so
   * one hand-edited day cannot fail the whole detail; `restaurantSeatingsOn`
   * parses it defensively and reads a bad week as unknown hours.
   */
  openingHours: z.unknown().optional(),
  tablePolicy: tablePolicySchema.nullish(),
  /** Transfer routes only; null elsewhere. */
  transfer: transferRouteConfigSchema.nullish(),
  /** Women-only group trip details; null for every other activity. */
  womenTrip: z
    .object({
      seatLimit: z.number().nullable(),
      depositAmount: z.number().nullable(),
      tripDates: z.array(z.object({ date: z.string(), seatsLeft: z.number().nullable() })),
      departures: z.array(z.string()).default([]),
      organiser: z
        .object({
          slug: z.string(),
          name: z.string(),
          tagline: z.string().nullable(),
          instagramHandle: z.string().nullable(),
          avatarUrl: z.string().nullable(),
        })
        .nullable(),
    })
    .nullish(),
  contentBlocks: z.record(z.string(), z.unknown()).default({}),
  /**
   * A restaurant's link to its own menu. Declared here because zod strips
   * undeclared keys — the admin field has existed (and saved) for months while
   * the value was dropped before it ever reached the page.
   */
  menuLink: z.string().nullable().default(null),
  /**
   * Short fact pills (localized). Declared here because zod strips undeclared
   * keys: the mobile preview sheet read `detail.facts` through a cast, so the
   * pills silently never rendered even for rows that carry facts in the DB.
   */
  facts: z.array(z.string()).default([]),
  packages: z.array(activityDetailPackageSchema).default([]),
  faq: z.array(z.object({ q: z.string(), a: z.string() })).default([]),
  timeSlots: z.array(z.object({ id: z.string(), label: z.string() })).default([]),
});
export type ActivityDetail = z.infer<typeof activityDetailSchema>;

export const categorySchema = z.object({
  id: z.string(),
  name: z.string(),
  // Optional because several producers of Category predate it; the Home row
  // needs it to sort by the user's picked interests.
  slug: z.string().nullable().optional(),
  thumbnail: z.string().url().nullable(),
});
export type Category = z.infer<typeof categorySchema>;

export const citySchema = z.object({
  id: z.string(),
  name: z.string(),
  image: z.string().url().nullable(),
  activitiesCount: z.number().int().nonnegative().optional(),
});
export type City = z.infer<typeof citySchema>;

export const comboSchema = z.object({
  id: z.string(),
  slug: z.string(),
  title: z.string(),
  image: z.string().url().nullable(),
  price: z.number().nonnegative(),
  currency: z.string(),
});
export type Combo = z.infer<typeof comboSchema>;

export const adSchema = z.object({
  id: z.string(),
  image: z.string().url(),
  link: z.string().url().nullable(),
});
export type Ad = z.infer<typeof adSchema>;

export const packageSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number().nonnegative(),
  priceType: z.enum(["person", "hour", "room", "group"]),
  transportPrice: z.number().nonnegative().default(0),
  includedBenefits: z.array(z.string()).default([]),
  excludedBenefits: z.array(z.string()).default([]),
  pricePerAdult: z.number().nonnegative().optional(),
  pricePerChild: z.number().nonnegative().optional(),
  pricePerInfant: z.number().nonnegative().optional(),
  pricePerHour: z.number().nonnegative().optional(),
  pricePerRoom: z.number().nonnegative().optional(),
  pricePerGroup: z.number().nonnegative().optional(),
});
export type Package = z.infer<typeof packageSchema>;

export const timeSlotSchema = z.object({
  id: z.string(),
  label: z.string(),
  hours: z.array(z.string()).default([]),
});
export type TimeSlot = z.infer<typeof timeSlotSchema>;

export const packagesRequestSchema = z.object({
  time_id: z.string(),
  date: z.number(),
  currency: z.string(),
});
export const packagesResponseSchema = z.object({
  packages: z.array(packageSchema),
  time: timeSlotSchema,
  hours: z.array(z.string()),
});

export * from "./bookable";
export * from "./is-stay";
export * from "./subtitle";
